import { MAPPING_TYPE } from 'Pages/User/CustomMapping/@constants'
import { selectedMappingTab } from 'Pages/User/CustomMapping/@signals'

// The open tab's "Create custom ACRISS" / "Create custom Category" button, in its table's toolbar
function findCreateButton() {
	return [...document.querySelectorAll('main button')].find(b => /^Create custom\b/.test(b.textContent.trim())) || null
}

// The open tab's mapping list (real or demo): the card that holds its create button
function findMappingTable() {
	const btn = findCreateButton()
	return btn?.closest('[class*="MUIDataTable-paper"]') || btn?.closest('.MuiPaper-root') || null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'diagram-project',
		title: ES ? 'Bienvenido a Mapeo personalizado' : 'Welcome to Custom Mapping',
		description: ES
			? 'El Mapeo personalizado te permite definir cómo se asignan los códigos ACRISS y las categorías de vehículos en función de criterios específicos como oficinas, países, sitios, proveedores, modelos de coche y características del vehículo. Crea mapeos uno a uno o súbelos en bloque mediante XLSX.'
			: 'Custom Mapping lets you define how ACRISS codes and vehicle categories are mapped based on specific criteria like offices, countries, sites, providers, car models, and vehicle features. Create mappings one by one or upload them in bulk via XLSX.',
	},
	{
		type: 'field',
		findTarget: () => document.querySelector('main .MuiTabs-root'),
		icon: 'table-columns',
		title: ES ? 'Pestañas de mapeo' : 'Mapping Tabs',
		description: ES
			? 'Un listado por pestaña: «Custom ACRISS» asigna códigos ACRISS y «Custom Categories» asigna categorías de vehículos. Los botones y la tabla de abajo son los de la pestaña abierta.'
			: 'One list per tab: "Custom ACRISS" assigns ACRISS codes and "Custom Categories" assigns vehicle categories. The buttons and the table below belong to the open tab.',
	},
	{
		type: 'field',
		findTarget: () => {
			return findCreateButton()?.parentElement || null
		},
		icon: 'toolbox',
		title: ES ? 'Botones de acción' : 'Action Buttons',
		description: ES
			? 'Las tres acciones de la pestaña abierta, en la barra de su tabla. «Upload XLSX»: importación masiva de mapeos desde una hoja de cálculo con vista previa de validación. «Download Example»: obtén una plantilla XLSX para rellenar. «Create custom ACRISS» / «Create custom Category»: abre un panel lateral para definir un nuevo mapeo con todos los criterios.'
			: 'The open tab\'s three actions, in its table\'s toolbar. "Upload XLSX": bulk import mappings from a spreadsheet with validation preview. "Download Example": get a template XLSX file to fill in. "Create custom ACRISS" / "Create custom Category": opens a side drawer to define a new mapping with all criteria.',
		example: '📄 Upload XLSX | 📥 Download Example | ➕ Create custom',
	},
	{
		type: 'field',
		findTarget: () => {
			return findMappingTable()
		},
		// the lists are tabs: the next step looks at Categories, so open it
		action: () => { selectedMappingTab.value = MAPPING_TYPE.CATEGORY },
		waitFor: () => /Category$/.test(findCreateButton()?.textContent.trim() || ''),
		icon: 'table-cells',
		title: ES ? 'Tabla de mapeo ACRISS' : 'ACRISS Mapping Table',
		description: ES
			? 'Todos tus mapeos ACRISS personalizados. Cada fila define una regla de mapeo con criterios: oficinas, países, sitios, código ACRISS, proveedores, modelo de coche, puertas, plazas, transmisión, A/C y quién es el propietario (usuario o empresa). Usa la búsqueda y los filtros de columna para encontrar mapeos específicos.'
			: 'All your custom ACRISS mappings. Each row defines a mapping rule with criteria: offices, countries, sites, the ACRISS code, providers, car model, doors, seats, transmission, A/C, and who owns it (user or company). Use search and column filters to find specific mappings.',
	},
	{
		type: 'field',
		findTarget: () => {
			return findMappingTable()
		},
		icon: 'tags',
		title: ES ? 'Tabla de mapeo de categorías' : 'Categories Mapping Table',
		description: ES
			? 'Misma estructura que ACRISS pero para categorías de vehículos. Define cómo se asignan las categorías en función de los mismos criterios: oficinas, países, sitios, proveedores, modelo de coche y características del vehículo. Los campos vacíos se tratan como «Any» (coincide con todo).'
			: 'Same structure as ACRISS but for vehicle categories. Define how categories are assigned based on the same criteria: offices, countries, sites, providers, car model, and vehicle features. Fields left empty are treated as "Any" (match all).',
	},
	{
		type: 'field',
		findTarget: () => {
			const icon = document.querySelector('.fa-pen') || document.querySelector('[data-icon="pen"]')
			if (icon) {
				const btn = icon.closest('button')
				if (btn?.parentElement) {
					const row = btn.parentElement
					if (row.children.length >= 2) return row
				}
				return btn
			}
			return null
		},
		skipIf: () => !document.querySelector('.fa-pen') && !document.querySelector('[data-icon="pen"]'),
		icon: 'pen-to-square',
		title: ES ? 'Editar y eliminar' : 'Edit & Delete',
		description: ES
			? 'Cada fila tiene botones de acción. Editar (lápiz): abre el panel lateral con todos los valores actuales precargados. Eliminar (papelera): borra el mapeo tras confirmación. Selecciona varias filas para eliminarlas en bloque con «Delete» en la barra flotante que aparece al pie de la pantalla.'
			: 'Each row has action buttons. Edit (pencil): opens the side drawer with all current values pre-filled. Delete (trash): removes the mapping after confirmation. Select multiple rows to delete them in bulk with "Delete" on the floating bar that appears at the bottom of the screen.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Crea mapeos individualmente o súbelos en bloque mediante XLSX. Descarga primero la plantilla de ejemplo para ver el formato correcto. Los campos vacíos actúan como comodines («Any»). Usa el panel de edición para modificar mapeos existentes, o la selección múltiple para eliminar varios a la vez.'
			: 'Create mappings individually or upload in bulk via XLSX. Download the example template first to see the correct format. Empty fields act as wildcards ("Any"). Use the edit drawer to modify existing mappings, or multi-select to delete several at once.',
	},
]
